import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { FvButton, Icon } from '@fv/client-components'

import { ValidatedForm } from '@/components/inputs'
import { PillHeader } from '@/components/PillHeader'

import { useAccountBrand, useAccountBrands } from '../company-branding/api'
import { BrandingSelector } from '../company-branding/BrandingSelector'
import { useBookingForm } from './hooks'
import { TLBookingSection } from './TLBookingSection'
import { brandingKey } from './types'

export const BookingBranding = () => {
  const { setFormRef, setFormSections, setBrandId, ...form } = useBookingForm()
  const { data: brands } = useAccountBrands()
  const selectedBrand = useAccountBrand(form.brandId)
  const formRef = useRef<HTMLFormElement>(null)
  const isEditing = form.activeSection === brandingKey
  const section = form.formSections.find(s => s.key === brandingKey)
  const hasBrands = (brands?.length ?? 0) > 1

  useEffect(() => {
    setFormSections(prev => {
      const hasSection = prev.some(s => s.key === brandingKey)

      if (!hasBrands) {
        return hasSection ? prev.filter(s => s.key !== brandingKey) : prev
      }

      if (hasSection) return prev

      return prev.concat({
        key: brandingKey,
        isValid: false,
        ref: null,
      })
    })
  }, [hasBrands, setFormSections])

  const hasSection = !!section

  useEffect(() => {
    if (!hasSection) return
    setFormRef(brandingKey, formRef)
  }, [hasSection, setFormRef])

  if (!hasSection) return null

  function editBranding() {
    form.activateSection(brandingKey)
  }

  return (
    <TLBookingSection isEditing={isEditing} isValid={section.isValid}>
      <PillHeader
        st
        fixedWidthIcon
        className={clsx(
          'beer-container -top-3.5 z-10 mb-3 flex min-h-[3.5rem] items-center !px-2',
          !isEditing &&
            !section.isValid &&
            '-ml-[calc(.5rem)] w-[calc(100%-3rem)]',
          isEditing && 'absolute left-[calc(1.5rem)] w-[calc(100%-7rem)]',
          section.isValid &&
            'absolute -left-[calc(.5rem+1px)] w-[calc(100%-3rem+1px)]',
        )}
      >
        {section.isValid && (
          <FvButton
            theme="round"
            onClick={() => editBranding()}
            icon="pen-alt"
          />
        )}
        <div
          className={clsx(
            'flex items-center',
            (isEditing || !section.isValid) && 'ml-2',
          )}
        >
          <Icon icon="dot-circle" className="text-fv-gray-dark" />
          <span>Branding</span>
        </div>
      </PillHeader>

      {isEditing && (
        <ValidatedForm onValidSubmit={form.goToNextSection} ref={formRef}>
          <div>
            <BrandingSelector
              value={form.brandId}
              onChange={setBrandId}
              className="mb-8"
            />
          </div>

          <FvButton
            theme="secondary"
            icon="arrow-down-to-line"
            fwd
            type="submit"
          >
            Looks good, proceed
          </FvButton>
        </ValidatedForm>
      )}

      {section.isValid && (
        <div className="text-sm text-fv-gray-dark">
          {selectedBrand ? selectedBrand.name : 'Default brand'}
        </div>
      )}
    </TLBookingSection>
  )
}
